// =====================================================================
// Aba 4 — Convênios (Fase 3, S38).
// CRUD clinic_benefits. Cada benefit pode ser vinculado a um
// profissional especifico OU "todos" (professional_id null).
// Required: name.
// =====================================================================
import React, { useState, useEffect } from "react";
import { Chip } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

import CrudTable from "../components/CrudTable";
import CrudDialog from "../components/CrudDialog";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import WeekdaysPicker from "../components/WeekdaysPicker";
import useCrudResource from "../hooks/useCrudResource";

import {
  listBenefits,
  createBenefit,
  updateBenefit,
  deleteBenefit,
  listProfessionals
} from "../../../services/clinicAdminService";

// CSV de days → string amigavel "Seg, Ter, Qua"
const formatDays = (csv) => {
  if (!csv) return "—";
  const map = {
    monday: "Seg", tuesday: "Ter", wednesday: "Qua",
    thursday: "Qui", friday: "Sex", saturday: "Sáb", sunday: "Dom"
  };
  return String(csv)
    .split(",")
    .map((d) => map[d.trim().toLowerCase()] || d)
    .join(", ");
};

const ConveniosTab = ({ slug }) => {
  const crud = useCrudResource({
    slug,
    list: listBenefits,
    create: createBenefit,
    update: updateBenefit,
    remove: deleteBenefit,
    resourceLabel: "convênio"
  });

  // Lista de profissionais pra popular o select "vincular a profissional"
  const [professionals, setProfessionals] = useState([]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    listProfessionals(slug)
      .then((data) => {
        if (cancelled) return;
        const arr = Array.isArray(data) ? data : data?.rows || [];
        setProfessionals(arr.filter((p) => p.is_active !== false));
      })
      .catch(() => {
        if (!cancelled) setProfessionals([]);
      });
    return () => { cancelled = true; };
  }, [slug]);

  const profMap = professionals.reduce((acc, p) => {
    acc[p.id] = p.name;
    return acc;
  }, {});

  const COLUMNS = [
    { key: "name", label: "Convênio / Benefício" },
    {
      key: "professional_id",
      label: "Profissional",
      width: 220,
      render: (r) => r.professional_id ? (profMap[r.professional_id] || `#${r.professional_id}`) : "Todos"
    },
    {
      key: "available_days",
      label: "Dias",
      width: 200,
      render: (r) => formatDays(r.available_days)
    },
    {
      key: "available_hours_start",
      label: "Horário",
      width: 120,
      render: (r) =>
        r.available_hours_start && r.available_hours_end
          ? `${String(r.available_hours_start).slice(0,5)} - ${String(r.available_hours_end).slice(0,5)}`
          : "—"
    },
    {
      key: "is_active",
      label: "Ativo",
      width: 70,
      align: "center",
      render: (r) =>
        r.is_active ? <CheckCircleIcon fontSize="small" htmlColor="#06c270" /> : "—"
    }
  ];

  const FIELDS = [
    {
      key: "name",
      label: "Nome do convênio/benefício",
      type: "text",
      required: true,
      placeholder: "Unimed Recife, Amil 400, Particular com desconto"
    },
    { key: "description", label: "Descrição", type: "textarea", rows: 2 },
    {
      key: "professional_id",
      label: "Profissional vinculado",
      type: "select",
      colSpan: 6,
      options: [
        { value: "", label: "Todos os profissionais" },
        ...professionals.map((p) => ({ value: p.id, label: p.name }))
      ],
      transform: (v) => (v === "" || v == null ? null : Number(v)),
      helperText: "Deixe em branco se vale pra qualquer profissional"
    },
    {
      key: "sort_order",
      label: "Ordem de exibição",
      type: "number",
      colSpan: 6,
      transform: (v) => (v === "" ? null : Number(v))
    },
    {
      key: "available_days",
      label: "Dias da semana disponíveis",
      type: "custom",
      render: (value, setValue) => (
        <WeekdaysPicker value={value} onChange={setValue} />
      ),
      helperText: "Em branco = qualquer dia"
    },
    {
      key: "available_hours_start",
      label: "Horário inicial",
      type: "text",
      colSpan: 4,
      placeholder: "08:00",
      regex: /^\d{2}:\d{2}(:\d{2})?$/,
      regexError: "Formato HH:MM (ex 08:00)"
    },
    {
      key: "available_hours_end",
      label: "Horário final",
      type: "text",
      colSpan: 4,
      placeholder: "18:00",
      regex: /^\d{2}:\d{2}(:\d{2})?$/,
      regexError: "Formato HH:MM (ex 18:00)"
    },
    { key: "is_active", label: "Ativo", type: "switch", colSpan: 4 }
  ];

  return (
    <>
      <CrudTable
        columns={COLUMNS}
        rows={crud.rows}
        loading={crud.loading}
        onAdd={crud.handleAdd}
        onEdit={crud.handleEdit}
        onDelete={crud.handleDelete}
        searchPlaceholder="Buscar convênio…"
        searchFields={["name", "description"]}
      />

      <CrudDialog
        open={crud.dialogOpen}
        onClose={crud.handleCloseDialog}
        onSave={crud.handleSave}
        title={crud.dialogInitial ? "Editar convênio" : "Novo convênio"}
        fields={FIELDS}
        initialValues={crud.dialogInitial}
        saving={crud.saving}
      />

      <ConfirmDeleteDialog
        open={!!crud.deleteTarget}
        onClose={crud.handleCancelDelete}
        onConfirm={crud.handleConfirmDelete}
        itemLabel={crud.deleteTarget?.name}
        saving={crud.saving}
      />
    </>
  );
};

export default ConveniosTab;
